import {Box} from "@mui/material";
import {useTranslation} from "react-i18next";
import {PaymentInformationDto, PaymentInformationFormat} from "../../../../client/payment/PaymentInformationApiClient";
import {CurrencyFormatter} from "../../../../common/CurrencyFormatter";
import {DetailsTextRow} from "../../../../common/details/DetailsTextRow";
import {PaymentTranslKey} from "../../PaymentTranslKey";
import {PaymentDetailsTable} from "./PaymentDetailsTable";
import {PaymentQrCode} from "./PaymentQrCode";

interface PaymentInformationDetailsProps {
    paymentInformation: PaymentInformationDto;
}

export function PaymentInformationDetails({paymentInformation}: Readonly<PaymentInformationDetailsProps>) {
    const {t} = useTranslation();

    const formatAmountWithCurrency = (amount?: number, currency?: string): string | undefined => {
        if (!amount) {
            return undefined;
        }
        if (!currency) {
            return `${amount}`;
        }
        return `${amount} ${currency}`;
    };

    return (
            <PaymentDetailsTable
                    header={t(PaymentTranslKey.PAYMENT_INFORMATION_TITLE)}
                    prologue={
                        <Box>
                            {paymentInformation.paymentText?.prologue && (
                                    <div>{paymentInformation.paymentText.prologue}</div>
                            )}
                            {paymentInformation.paymentText?.mainText && (
                                    <div>{paymentInformation.paymentText.mainText}</div>
                            )}
                        </Box>
                    }
                    epilogue={
                        <Box fontWeight="bold" sx={{whiteSpace: "pre-line"}}>
                            {paymentInformation.paymentText?.epilogue}
                        </Box>
                    }
                    qrCode={paymentInformation.format !== PaymentInformationFormat.ONLY_TEXTS && paymentInformation.accountNumber && (
                            <PaymentQrCode
                                    accountNumber={paymentInformation.accountNumber}
                                    variableSymbol={paymentInformation.variableSymbol}
                                    specificSymbol={paymentInformation.specificSymbol}
                                    amount={paymentInformation.amount}
                                    message={paymentInformation.messageForRecipient}
                            />
                    )}
            >
                {paymentInformation.format !== PaymentInformationFormat.ONLY_TEXTS && (
                        <>
                            <DetailsTextRow
                                    label={t(PaymentTranslKey.ACCOUNT_NUMBER)}
                                    value={paymentInformation.accountNumber}
                                    comment={paymentInformation.accountNumberComment}
                            />
                            <DetailsTextRow
                                    label={t(PaymentTranslKey.VARIABLE_SYMBOL)}
                                    value={paymentInformation.variableSymbol}
                                    comment={paymentInformation.variableSymbolComment}
                            />
                            <DetailsTextRow
                                    label={t(PaymentTranslKey.SPECIFIC_SYMBOL)}
                                    value={paymentInformation.specificSymbol}
                                    comment={paymentInformation.specificSymbolComment}
                            />
                            <DetailsTextRow
                                    label={t(PaymentTranslKey.AMOUNT)}
                                    value={formatAmountWithCurrency(paymentInformation.amount, CurrencyFormatter.formatCurrencyFromCode(paymentInformation.currency, t))}
                                    comment={paymentInformation.amountComment}
                            />
                            <DetailsTextRow
                                    label={t(PaymentTranslKey.MESSAGE_FOR_RECIPIENT)}
                                    value={paymentInformation.messageForRecipient}
                                    comment={paymentInformation.messageForRecipientComment}
                            />
                        </>
                )}
            </PaymentDetailsTable>
    );
}
